import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SortChangedEvent,
} from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  EntityListField,
  FacadeInterface,
  FilterConfig,
  getExcelFields,
  getGridColumnType,
  mapFromFilterModel,
  mapToFilterModel,
  SharedFacadeInterface,
} from '@ellf/app-common';
import { RouterService } from '@ellf/app-routing';
import {
  CustomGridFilterComponent,
  CustomGridFilterParams,
  DataGridAction,
} from '@ellf/app-ui-kit';
import { BaseDbDto } from '@ellf/common-dto';
import { FieldConfig, FilterType, FilterValue } from '@ellf/common-interfaces';
import { FilterCustomPermissionsEnum, filterNullish } from '@ellf/common-utils';
import { cloneDeep, isEqual } from 'lodash';
import { unbox } from 'ngrx-forms';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  combineLatest,
  firstValueFrom,
  from,
  map,
  Observable,
  shareReplay,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { BaseContainer } from './base-container';

@Component({
  template: '',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BaseListContainer<T extends BaseDbDto>
  extends BaseContainer
  implements OnInit
{
  abstract entity: string;
  abstract sectionTitle: string;

  join?: string[];
  exportJoin?: string[];

  abstract fields$: Observable<EntityListField[]>;
  customActions: DataGridAction[] = this.routerService.isFindMode()
    ? [
        {
          description: 'seleziona',
          class: 'icon-arrow-left-circle',
          onAction: (entity: any) => {
            this.select(entity);
          },
          visible: (entity: any) => true,
        },
      ]
    : [];

  createPermissions: string[] = [];
  readPermissions: string[] = [];
  updatePermissions: string[] = [];
  deletePermissions: string[] = [];

  skipFilterPermission$ = this.facade.getQueryParams$.pipe(
    map((queryParams) => {
      const filters = queryParams['skipFilterPermission'];
      if (!filters) return [];
      const decoded = atob(queryParams['skipFilterPermission']);
      const skipFilterPermission = JSON.parse(
        decoded,
      ) as FilterCustomPermissionsEnum[];

      return skipFilterPermission;
    }),
  );

  presetFilters$ = this.facade.getQueryParams$.pipe(
    map((queryParams) => {
      const filters = queryParams['filters'];
      if (!filters) return [];
      const decoded = atob(queryParams['filters']);
      const queryParamsFilters = JSON.parse(decoded) as FilterValue[];

      return queryParamsFilters;
    }),
  );

  // abstract filters: FilterConfig[];
  // abstract colDefs: ColDef[];

  private _filters?: Observable<FilterConfig[]>;
  get filters$() {
    if (!this._filters) {
      this._filters = combineLatest([this.fields$, this.presetFilters$]).pipe(
        map(([fields, presetFilters]) =>
          fields
            .map((f) => ({
              ...f,
              readOnly:
                presetFilters.find((pf) => pf.field === f.field) != null,
            }))
            .filter(
              (f) =>
                !(
                  f.hideFilter &&
                  presetFilters.find((pf) => pf.field === f.field) == null
                ),
            ),
        ),
      );
      // .map((f) => {
      //   return {
      //     elementLabel: f.elementLabel,
      //     field: f.field,
      //     filterType: f.filterType,
      //     elementValues$: f.elementValues$,
      //   } as FilterConfig;
      // });
    }
    return this._filters;
  }

  private _fieldToExport?: Observable<FieldConfig[]>;
  get fieldToExport$() {
    if (!this._fieldToExport) {
      this._fieldToExport = combineLatest([
        this.fields$,
        this.facade.getColumnState$.pipe(filterNullish()),
      ]).pipe(
        map(([fields, columnState]) => {
          return getExcelFields(fields, columnState);
        }),
      );
    }
    return this._fieldToExport;
  }

  private _columns?: Observable<ColDef[]>;
  get columnDefs$() {
    if (!this._columns) {
      this._columns = combineLatest([this.fields$, this.presetFilters$]).pipe(
        map(([fields, presetFilters]) =>
          fields
            .filter((f) => {
              return (
                !f.hideColumn ||
                presetFilters.find((pf) => pf.field === f.field) != null
              );
            })
            .map((f) => {
              return {
                colId: f.field,
                field: f.field,
                initialHide:
                  f.initialHideColumn &&
                  presetFilters.find((pf) => pf.field === f.field) == null,
                headerName: f.elementLabel,
                type: getGridColumnType(f.filterType, f.fieldType),
                filterParams: { suppressAndOrCondition: true },
                cellRendererParams: {
                  elementValues: f.elementValues,
                },
                floatingFilterComponentParams: {
                  suppressFilterButton: true,
                  values: f.elementValues,
                  filterType: f.filterType,
                  field: f.field,
                  entityName: f.entityName,
                  readOnly:
                    presetFilters.find((pf) => pf.field === f.field) != null,
                } as CustomGridFilterParams,
                sortable: f.sortable,
                floatingFilter: f.sortable === false ? false : undefined,
                filter: f.sortable === false ? false : undefined,
                sort: f.sort,
                maxWidth: f.maxWidth,
                minWidth:
                  (f.minWidth ??
                  (f.filterType === FilterType.date ||
                    f.filterType === FilterType.datetime))
                    ? 240
                    : 200,
                valueGetter: f.valueGetter ? f.valueGetter : undefined,
                autoHeight: true,
                cellStyle: { 'white-space': 'normal' },
              } as ColDef;
            }),
        ),
      );
    }
    return this._columns;
    // return this.entityFilters?.filter((ef) => ef.isFilter);
  }
  // columnDefs: ColDef[] = [];

  protected gridApi?: GridApi;

  commonGridOptions: GridOptions = {
    floatingFiltersHeight: 70,
    pagination: true,
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Colonne',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true,
          },
        },
      ],
      // defaultToolPanel: 'columns',
    },
    suppressPaginationPanel: true,
    onGridReady: (e: GridReadyEvent) => this.onGridReady(e),
    onFilterChanged: (e: FilterChangedEvent) => this.onFilterChanged(e),
    onSortChanged: (e: SortChangedEvent) => this.onColumnStateChanged(e),
    onColumnVisible: (e: ColumnVisibleEvent) => this.onColumnStateChanged(e),
    onColumnMoved: (e: ColumnMovedEvent) => this.onColumnStateChanged(e),
    onColumnResized: (e: ColumnResizedEvent) => this.onColumnStateChanged(e),
    defaultColDef: {
      floatingFilter: true,
      resizable: true,
      filter: true,
      suppressMenu: true,
      floatingFilterComponent: 'customGridFilter',
    },
    components: {
      customGridFilter: CustomGridFilterComponent,
    },
    columnTypes: {
      numberColumn: {
        filter: 'agNumberColumnFilter',
      },
      enumColumn: {
        cellRenderer: 'gridComponentEnumRenderer',
      },
      dateColumn: {
        filter: 'agDateColumnFilter',
      },
    },
  };

  filterFormState$ = this.facade.selectFilterForm$;
  pageSize$ = this.facade.getPageSize$;
  currentPage$ = this.facade.getCurrentPage$;
  totalRows$ = this.facade.getTotalRows$;
  filterModel$ = this.facade.getFilterModel$;

  canAdd$ = new Observable<boolean>();
  canEdit$ = new Observable<boolean>();
  canView$ = new Observable<boolean>();
  canDelete$ = new Observable<boolean>();
  canDownloadExcel$ = new Observable<boolean>();
  canDownloadPdf$ = new Observable<boolean>();

  constructor(
    @Inject('SharedEntityFacade')
    protected sharedFacade: SharedFacadeInterface<T>,
    @Inject('EntityFacade')
    protected facade: FacadeInterface<T>,
    protected routerService: RouterService,
    protected permissionsService: NgxPermissionsService,
    protected route?: ActivatedRouteSnapshot,
  ) {
    super();
  }

  ngOnInit(): void {
    const isFindMode = this.routerService.isFindMode();

    const permissions$ = combineLatest([
      from(this.permissionsService.hasPermission(this.createPermissions)),
      from(this.permissionsService.hasPermission(this.readPermissions)),
      from(this.permissionsService.hasPermission(this.updatePermissions)),
      from(this.permissionsService.hasPermission(this.deletePermissions)),
    ]).pipe(
      map(([canCreate, canRead, canUpdate, canDelete]) => ({
        canCreate: !isFindMode && canCreate,
        canRead: !isFindMode && canRead,
        canUpdate: !isFindMode && canUpdate,
        canDelete: !isFindMode && canDelete,
      })),
      shareReplay(1),
    );

    this.canAdd$ = permissions$.pipe(map((p) => p.canCreate));
    this.canEdit$ = permissions$.pipe(map((p) => p.canUpdate));
    this.canView$ = permissions$.pipe(map((p) => p.canRead));
    this.canDelete$ = permissions$.pipe(map((p) => p.canDelete));
    // stessi permessi di lettura
    this.canDownloadExcel$ = permissions$.pipe(map((p) => p.canRead));
    this.canDownloadPdf$ = permissions$.pipe(map((p) => p.canRead));
  }

  gridOptions$!: Observable<GridOptions>;

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    this.pageSize$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((pageSize) => {
      this.pageSizeChanged(event.api, pageSize);
    });

    combineLatest([this.facade.getFilterModel$, this.presetFilters$])
      .pipe(
        withLatestFrom(this.filterFormState$),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(([[filterModel, presetFilters], filterForm]) => {
        //console.log('filterModel', filterModel);
        const gridFilters = mapToFilterModel(presetFilters);
        filterModel = { ...filterModel, ...gridFilters };
        if (!isEqual(filterModel, this.gridApi?.getFilterModel())) {
          this.gridApi?.setFilterModel(filterModel);
        }

        const newFilters = mapFromFilterModel(filterModel);
        const filterFormValue = unbox(filterForm.value.filters);
        if (!isEqual(newFilters, filterFormValue)) {
          this.facade.setFilterFormValue(newFilters);
        }
      });

    this.facade.getColumnState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sortModel) => {
        if (sortModel && !isEqual(sortModel, event.api.getColumnState())) {
          event.api.applyColumnState({
            // state: sortModel,
            state: cloneDeep(sortModel),
            applyOrder: true,
          });
        }
      });

    combineLatest([this.currentPage$, this.facade.pageLoaded$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([page, loaded]) => {
        if (loaded) {
          this.gridApi?.paginationGoToPage(page - 1);
        }
      });
  }

  onPageChange(newPage: number) {
    this.facade.setCurrentPage(newPage);
  }

  onPageSizeChanged(size: number) {
    this.facade.setPageSize(size);
  }

  pageSizeChanged(api: GridApi, pageSize: number) {
    api.setGridOption('cacheBlockSize', pageSize);
    // api.purgeInfiniteCache();
    api.setGridOption('paginationPageSize', pageSize);
  }

  onColumnStateChanged(
    e:
      | SortChangedEvent
      | ColumnVisibleEvent
      | ColumnMovedEvent
      | ColumnResizedEvent,
  ) {
    this.facade.setColumnState(cloneDeep(e.api.getColumnState()));
    // this.facade.setColumnState(e.api.getColumnState());
  }

  // Ricerca da griglia
  onFilterChanged(e: FilterChangedEvent) {
    this.facade.setFilterModel(e.api.getFilterModel());
  }

  // Ricerca da form esterno
  search(filters: FilterValue[]) {
    const externalFilters = mapToFilterModel(filters);
    this.facade.setFilterModel(externalFilters);
  }

  async pdf(fileName: string) {
    const columns = await firstValueFrom(this.fieldToExport$);

    this.facade.downloadPdf(
      fileName,
      columns,
      this.exportJoin ? this.exportJoin : this.join,
    );
  }

  async excel(fileName: string) {
    const columns = await firstValueFrom(this.fieldToExport$);

    this.facade.downloadExcel(
      fileName,
      columns,
      this.exportJoin ? this.exportJoin : this.join,
    );
  }

  // protected mapToFilterModel(
  //   filterModel: AgGridFilterModel,
  //   filters: FilterValue[]
  // ) {
  //   return filters.reduce((res, f) => {
  //     res[f.field] = {
  //       type: FilterOperator[f.operator],
  //       filterType: FilterType[f.type],
  //       filter: f.value.length ? f.value[0] : undefined,
  //     };
  //     return res;
  //   }, filterModel);
  // }

  // protected mapFromFilterModel(filterModel: AgGridFilterModel) {
  //   const filters: FilterValue[] = [];
  //   Object.keys(filterModel).forEach((key) => {
  //     const gridFilter = filterModel[key];
  //     filters.push({
  //       field: key,
  //       operator: (<any>FilterOperator)[gridFilter.type],
  //       type: (<any>FilterType)[gridFilter.filterType],
  //       value: [gridFilter.filter],
  //     });
  //   });
  //   return filters;
  // }

  add() {
    this.facade.formInitialize(undefined);
    this.routerService.router.navigate([this.entity, '0']);
  }

  edit(entity: T) {
    this.routerService.router.navigate([this.entity, entity.id]);
  }

  view(entity: T) {
    this.routerService.router.navigate([this.entity, entity.id]);
    //this.routerService
  }

  delete(entity: T) {
    if (confirm('Confermi di voler eliminare questo elemento?')) {
      this.sharedFacade.delete(entity.id, true, false, () => {
        // refresh lista paginata
        if (this.gridApi) {
          this.gridApi.refreshServerSide({}); // { route: route, purge: purge }
        }
      });
    }
  }

  select(item: T) {
    this.routerService.notifySelectedItemToOpener(item);
  }

  refreshList() {
    // refresh lista paginata
    if (this.gridApi) {
      this.gridApi.refreshServerSide({}); // { route: route, purge: purge }
    }
  }
}
